import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as Tone from 'tone';

// --- Melody Library Types & Helpers ---
interface MelodyEntry {
  name: string;
  key: string;
  tuning?: string;
  capo?: number;
  bpm: number;
  timeSignature?: string;
  transcribedAt: string;
  notes: { note: string; duration: string; beats: number; isRest?: boolean }[];
}

const NOTE_NAMES_ALL = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

function transposeNoteStr(note: string, semitones: number): string {
  const match = note.match(/^([A-G]#?)(\d)$/);
  if (!match) return note;
  const [, name, octStr] = match;
  const idx = NOTE_NAMES_ALL.indexOf(name);
  const newIdx = idx + semitones;
  const newOctave = parseInt(octStr) + Math.floor(newIdx / 12);
  const newNote = NOTE_NAMES_ALL[((newIdx % 12) + 12) % 12];
  return `${newNote}${newOctave}`;
}

function getTransposedKey(originalKey: string, semitones: number): string {
  const idx = NOTE_NAMES_ALL.indexOf(originalKey);
  if (idx === -1) return originalKey;
  return NOTE_NAMES_ALL[((idx + semitones) % 12 + 12) % 12];
}

// --- Instrument Definitions ---
type InstrumentType = 'banjo' | 'guitar' | 'fiddle' | 'tenor';

interface InstrumentConfig {
  name: string;
  numFrets: number;
  strings: number;
  tunings: Record<string, string[]>;
  defaultTuning: string;
  fretless?: boolean;
  fifthString?: { startFret: number };
  fretDots?: number[];
}

const INSTRUMENTS: Record<InstrumentType, InstrumentConfig> = {
  banjo: {
    name: 'Banjo',
    numFrets: 22,
    strings: 5,
    tunings: {
      'Open G': ['G4', 'D3', 'G3', 'B3', 'D4'],
      'Double C': ['G4', 'C3', 'G3', 'C4', 'D4'],
      'Sawmill (G Modal)': ['G4', 'D3', 'G3', 'C4', 'D4'],
      'Open D': ['F#4', 'D3', 'F#3', 'A3', 'D4'],
      'Open A': ['A4', 'E3', 'A3', 'C#4', 'E4'],
      'Double D': ['A4', 'D3', 'A3', 'D4', 'E4'],
    },
    defaultTuning: 'Open G',
    fifthString: { startFret: 5 },
    fretDots: [3, 5, 7, 10, 12, 15, 17, 19, 22],
  },
  guitar: {
    name: 'Guitar',
    numFrets: 14,
    strings: 6,
    tunings: {
      'Standard': ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'],
      'Drop D': ['D2', 'A2', 'D3', 'G3', 'B3', 'E4'],
      'Open G': ['D2', 'G2', 'D3', 'G3', 'B3', 'D4'],
      'DADGAD': ['D2', 'A2', 'D3', 'G3', 'A3', 'D4'],
      'Open D': ['D2', 'A2', 'D3', 'F#3', 'A3', 'D4'],
    },
    defaultTuning: 'Standard',
    fretDots: [3, 5, 7, 9, 12],
  },
  fiddle: {
    name: 'Fiddle',
    numFrets: 11,
    strings: 4,
    tunings: {
      'Standard (GDAE)': ['G3', 'D4', 'A4', 'E5'],
      'AEAE': ['A3', 'E4', 'A4', 'E5'],
      'ADAE': ['A3', 'D4', 'A4', 'E5'],
      'GDGD': ['G3', 'D4', 'G4', 'D5'],
      'DDAD': ['D3', 'D4', 'A4', 'D5'],
      'AEAC#': ['A3', 'E4', 'A4', 'C#5'],
    },
    defaultTuning: 'Standard (GDAE)',
    fretless: true,
    fretDots: [3, 5, 7, 10],
  },
  tenor: {
    name: 'Tenor Guitar',
    numFrets: 18,
    strings: 4,
    tunings: {
      // Jamie's tuning (2026-10-29): G–D–G–B low→high.
      // Strings are stored low→high (string #4 = low G, string #1 = high B).
      'GDGB': ['G3', 'D4', 'G4', 'B4'],
    },
    defaultTuning: 'GDGB',
    fretDots: [3, 5, 7, 9, 12, 15],
  },
};

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// --- Theory Mode Constants ---
interface ScaleDef { label: string; intervals: number[]; starred?: boolean; }

const SCALES: Record<string, ScaleDef> = {
  major:            { label: '⭐ Major (Ionian)',     intervals: [0, 2, 4, 5, 7, 9, 11], starred: true },
  minor:            { label: '⭐ Natural Minor',      intervals: [0, 2, 3, 5, 7, 8, 10], starred: true },
  mixolydian:       { label: '⭐ Mixolydian',         intervals: [0, 2, 4, 5, 7, 9, 10], starred: true },
  dorian:           { label: '⭐ Dorian',             intervals: [0, 2, 3, 5, 7, 9, 10], starred: true },
  pentatonic_major: { label: '⭐ Major Pentatonic',   intervals: [0, 2, 4, 7, 9],        starred: true },
  pentatonic_minor: { label: '⭐ Minor Pentatonic',   intervals: [0, 3, 5, 7, 10],       starred: true },
  blues:            { label: '⭐ Blues',              intervals: [0, 3, 5, 6, 7, 10],    starred: true },
  lydian:           { label: 'Lydian',                intervals: [0, 2, 4, 6, 7, 9, 11] },
  phrygian:         { label: 'Phrygian',              intervals: [0, 1, 3, 5, 7, 8, 10] },
  harmonic_minor:   { label: 'Harmonic Minor',        intervals: [0, 2, 3, 5, 7, 8, 11] },
};

interface ChordDef { label: string; intervals: number[]; }

const CHORDS: Record<string, ChordDef> = {
  major: { label: 'Major',  intervals: [0, 4, 7] },
  minor: { label: 'Minor',  intervals: [0, 3, 7] },
  dom7:  { label: '7',      intervals: [0, 4, 7, 10] },
  maj7:  { label: 'Maj7',   intervals: [0, 4, 7, 11] },
  min7:  { label: 'Min7',   intervals: [0, 3, 7, 10] },
  sus2:  { label: 'Sus2',   intervals: [0, 2, 7] },
  sus4:  { label: 'Sus4',   intervals: [0, 5, 7] },
};

// Map semitone (0-11) to interval label
const INTERVAL_LABELS = ['1', 'b2', '2', 'b3', '3', '4', 'b5', '5', 'b6', '6', 'b7', '7'];

const DURATIONS: { label: string; value: string; beats: number }[] = [
  { label: '𝅝 Whole (4)', value: '1n', beats: 4 },
  { label: '𝅗𝅥 Half (2)', value: '2n', beats: 2 },
  { label: '♩ Quarter (1)', value: '4n', beats: 1 },
  { label: '♪ Eighth (½)', value: '8n', beats: 0.5 },
  { label: '𝅘𝅥𝅯 16th (¼)', value: '16n', beats: 0.25 },
  { label: '𝅘𝅥𝅰 32nd (⅛)', value: '32n', beats: 0.125 },
];

interface SequenceNote {
  id: string;
  note: string;
  duration: string;
  beats: number;
  isRest?: boolean;
}

interface SavedRiff {
  name: string;
  instrument?: string;
  tuning: string;
  capo?: number;
  bpm: number;
  timeSignature?: string;
  notes: SequenceNote[];
  savedAt: string;
}

function noteToMidi(note: string): number {
  const match = note.match(/^([A-G]#?)(\d)$/);
  if (!match) return 60;
  const [, name, octave] = match;
  return (parseInt(octave) + 1) * 12 + NOTE_NAMES.indexOf(name);
}

function midiToNote(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  const name = NOTE_NAMES[midi % 12];
  return `${name}${octave}`;
}

function getNoteName(note: string): string {
  const match = note.match(/^([A-G]#?)/);
  return match ? match[1] : '';
}

function beatsToSlots(beats: number): number {
  return Math.max(1, Math.round(beats * 8));
}

// --- User Guide Modal ---
// Overlay modal rendered via portal-free absolute positioning. Opened from the
// ❓ icon in the header. Content is written as JSX (not markdown) so we don't
// pull in a markdown lib for a single doc. Source of truth for the written
// guide lives at `stringforge/docs/USER_GUIDE.md` — keep this component in
// sync when that file changes.
const UserGuideModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  // Esc-to-close for keyboard users.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const section: React.CSSProperties = { marginBottom: '20px' };
  const h2: React.CSSProperties = { color: '#e94560', fontSize: '16px', margin: '0 0 8px 0', borderBottom: '1px solid #333', paddingBottom: '4px' };
  const h3: React.CSSProperties = { color: '#2a6e3f', fontSize: '14px', margin: '12px 0 4px 0' };
  const p: React.CSSProperties = { fontSize: '13px', lineHeight: '1.5', margin: '4px 0', color: '#ddd' };
  const code: React.CSSProperties = { background: '#0f3460', padding: '1px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px', color: '#e94560' };
  const li: React.CSSProperties = { fontSize: '13px', lineHeight: '1.5', margin: '2px 0', color: '#ddd' };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="StringForge User Guide"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#16213e', border: '1px solid #e94560', borderRadius: '8px',
          padding: '20px 24px', maxWidth: '720px', width: '100%',
          maxHeight: '85vh', overflowY: 'auto', color: '#eee',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ color: '#e94560', fontSize: '20px', margin: 0 }}>🎵 StringForge User Guide</h1>
          <button
            onClick={onClose}
            title="Close (Esc)"
            aria-label="Close user guide"
            style={{
              background: '#0f3460', color: '#eee', border: '1px solid #555',
              borderRadius: '4px', padding: '4px 10px', fontSize: '14px', cursor: 'pointer',
            }}
          >✕</button>
        </div>

        <div style={section}>
          <h2 style={h2}>What is StringForge?</h2>
          <p style={p}>StringForge is a browser-based composer and practice tool for stringed instruments — banjo, guitar, fiddle, and tenor guitar. Pick an instrument and tuning, see the fretboard, compose melodies note by note, play them back at any tempo, and explore scales and chords in Theory mode.</p>
        </div>

        <div style={section}>
          <h2 style={h2}>The three modes</h2>
          <h3 style={h3}>✏️ Compose</h3>
          <p style={p}>Click notes on the fretboard to add them to your sequence. Pick a duration (whole, half, quarter, 8th, 16th, 32nd) before clicking, or add a rest with the <span style={code}>𝑾 Rest</span> button. The sequence shows at the bottom; click any slot to replace or delete it.</p>
          <h3 style={h3}>▶️ Play</h3>
          <p style={p}>Playback mode. Hit <span style={code}>Play</span> to run the sequence, toggle <span style={code}>Loop</span> to repeat, enable the <span style={code}>Metronome</span> for a click track with your melody, or add a <span style={code}>1-Bar Count-in</span> before playback starts. The vertical cursor on the fretboard shows the current note.</p>
          <h3 style={h3}>🎓 Theory</h3>
          <p style={p}>Fretboard view with a chosen Key, Scale, and (optionally) Chord highlighted. Non-scale notes dim out; roots are red, 5ths are light red, other scale/chord tones are purple. Also includes a practice runner — Up/Down/Both direction, note duration, and a drone root so you can play along in-mode.</p>
        </div>

        <div style={section}>
          <h2 style={h2}>The toolbar</h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={li}><b>Mode</b> — Compose / Play / Theory.</li>
            <li style={li}><b>Instrument</b> — Banjo, Guitar, Fiddle, Tenor Guitar. Switching resets tuning + capo to that instrument's defaults.</li>
            <li style={li}><b>Tuning</b> — varies per instrument (see Instruments & tunings below).</li>
            <li style={li}><b>Capo</b> — fret 0–5. Hidden on Fiddle (fretless). Shifts the playable range.</li>
            <li style={li}><b>Time Sig</b> — 2/4, 3/4, 4/4.</li>
            <li style={li}><b>Note Duration</b> (Compose only) — duration of the next note you click.</li>
            <li style={li}><b>BPM</b> — playback tempo, 40–220.</li>
          </ul>
        </div>

        <div style={section}>
          <h2 style={h2}>The fretboard</h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={li}>Strings are labeled on the left: <b>5th–1st</b> on banjo (5th on top, 1st on the bottom — standard banjo convention). Guitar/Tenor label 6th–1st and 4th–1st respectively.</li>
            <li style={li}>Fret numbers run across the bottom. Fret 0 (open string) is at the left; the capo bar moves it rightward.</li>
            <li style={li}>Fret dots mark common position landmarks (3, 5, 7, 10, 12… varies per instrument).</li>
            <li style={li}>The <b>banjo 5th string</b> starts at fret 5 — that's the short drone string, drawn with its open position matching the regular strings' 5th fret.</li>
            <li style={li}>In Play mode, the vertical white bar is the current note cursor.</li>
          </ul>
        </div>

        <div style={section}>
          <h2 style={h2}>Playback controls</h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={li}><span style={code}>Play</span> — start/stop the sequence.</li>
            <li style={li}><span style={code}>Loop ON/OFF</span> — restart automatically at the end.</li>
            <li style={li}><span style={code}>Metro w/Melody</span> — add a metronome click underneath the melody.</li>
            <li style={li}><span style={code}>1-Bar Count-in</span> — four clicks before playback starts.</li>
            <li style={li}><span style={code}>Loop Count-in</span> — whether the count-in repeats every loop.</li>
          </ul>
        </div>

        <div style={section}>
          <h2 style={h2}>Instruments & tunings</h2>
          <h3 style={h3}>Banjo (default)</h3>
          <p style={p}>5-string, 22 frets. Tunings: <b>Open G</b> (gDGBD, default), Double C, Sawmill (G Modal), Open D, Open A, Double D. 5th string starts at fret 5.</p>
          <h3 style={h3}>Guitar</h3>
          <p style={p}>6-string, 14 frets. Tunings: <b>Standard</b> (EADGBE, default), Drop D, Open G, DADGAD, Open D.</p>
          <h3 style={h3}>Fiddle</h3>
          <p style={p}>4-string, fretless (11 position markers). Tunings: <b>Standard (GDAE)</b> (default), AEAE, ADAE, GDGD, DDAD, AEAC#.</p>
          <h3 style={h3}>Tenor Guitar</h3>
          <p style={p}>4-string, 18 frets. Tuning: <b>GDGB</b> (low→high: G3–D4–G4–B4). Added 2026-10-29 at Jamie's request.</p>
        </div>

        <div style={section}>
          <h2 style={h2}>Melody Library</h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={li}>Hit <span style={code}>📚 Melody Library</span> to open the library panel.</li>
            <li style={li}>Click a melody name to <b>load</b> it — instrument, tuning, capo, BPM, and the note sequence all restore.</li>
            <li style={li}>Save a new melody from Compose mode via the save/transfer button on the sequence row. Names must be unique (case-insensitive).</li>
            <li style={li}>Click the <span style={code}>✕</span> on a melody to delete it. (The library is auto-snapshotted on every change — last 20 states kept server-side, so deletes are recoverable.)</li>
            <li style={li}>A loaded melody can be transposed up/down a semitone at a time; the key label and all notes shift together.</li>
          </ul>
        </div>

        <div style={section}>
          <h2 style={h2}>Tips</h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={li}>Mode, instrument, tuning, and BPM all persist across page reloads — you come back to the setup you left.</li>
            <li style={li}>Theory mode remembers its own key/scale/chord selection separately from Compose mode's state.</li>
            <li style={li}>Press <span style={code}>Esc</span> or click outside this box to close the guide.</li>
          </ul>
        </div>

        <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', margin: '16px 0 0 0' }}>
          Full guide source: <span style={code}>stringforge/docs/USER_GUIDE.md</span>
        </p>
      </div>
    </div>
  );
};

const BanjoFretboard: React.FC = () => {
  const [instrument, setInstrument] = useState<InstrumentType>('banjo');
  const [tuning, setTuning] = useState('Open G');
  const [capo, setCapo] = useState(0);
  const [activeNotes, setActiveNotes] = useState<Set<string>>(new Set());
  const [selectedDuration, setSelectedDuration] = useState(DURATIONS[3]);
  const [sequence, setSequence] = useState<SequenceNote[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(true);
  const [bpm, setBpm] = useState(100);
  const [playingIndex, setPlayingIndex] = useState(-1);
  const [savedRiffs, setSavedRiffs] = useState<SavedRiff[]>([]);
  const [riffName, setRiffName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [mode, setMode] = useState<'play' | 'compose' | 'theory'>(() => {
    const saved = localStorage.getItem('stringforge-mode');
    return (saved === 'play' || saved === 'compose' || saved === 'theory') ? saved : 'play';
  });
  const [melodyLibrary, setMelodyLibrary] = useState<MelodyEntry[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [loadedMelody, setLoadedMelody] = useState<MelodyEntry | null>(null);
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [timeSignature, setTimeSignature] = useState('4/4');
  const [measuresPerRow, setMeasuresPerRow] = useState(2);
  const [selectedNotes, setSelectedNotes] = useState<Set<string>>(new Set());
  const [pitchEditId, setPitchEditId] = useState<string | null>(null);
  const [selectionAnchor, setSelectionAnchor] = useState<number | null>(null);
  const [clipboard, setClipboard] = useState<SequenceNote[]>([]);
  const [metronomeState, setMetronomeState] = useState<'off' | 'with-melody' | 'on'>('with-melody');
  const standaloneMetronomeRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const standaloneMetronomeBeatRef = useRef(0);
  const [loopCountIn, setLoopCountIn] = useState(false);
  const [doubleCountIn, setDoubleCountIn] = useState(false);
  // Theory mode state (persisted)
  const [theoryKey, setTheoryKey] = useState(() => localStorage.getItem('stringforge-theory-key') || 'G');
  const [theoryScale, setTheoryScale] = useState(() => localStorage.getItem('stringforge-theory-scale') || 'major');
  const [theoryChord, setTheoryChord] = useState(() => localStorage.getItem('stringforge-theory-chord') || 'none');
  const [showBlueNotes, setShowBlueNotes] = useState(false);
  const [fretRangeMin, setFretRangeMin] = useState(() => parseInt(localStorage.getItem('stringforge-fret-min') || '0'));
  const [fretRangeMax, setFretRangeMax] = useState(() => parseInt(localStorage.getItem('stringforge-fret-max') || '5'));
  const [theoryDirection, setTheoryDirection] = useState<'up' | 'down' | 'both'>(() => {
    const saved = localStorage.getItem('stringforge-theory-direction');
    return (saved === 'up' || saved === 'down' || saved === 'both') ? saved : 'both';
  });
  const [startFromRoot, setStartFromRoot] = useState<boolean>(() => {
    const saved = localStorage.getItem('stringforge-theory-start-from-root');
    return saved === null ? true : saved === 'true';
  });

  const synthRef = useRef<Tone.PolySynth | null>(null);
  const tickRef = useRef<Tone.Synth | null>(null);
  const tockRef = useRef<Tone.Synth | null>(null);
  const stopRef = useRef(false);
  const playingIndexRef = useRef(-1);

  // --- Drone (Theory mode): one always-on lower root voice, plus three optional voices
  // gated by independent toggles. Sits one octave below the lowest playable root on the
  // current tuning. Goal: drone sits under the fiddle, never collides with fingered notes.
  //   Lower root (always on with drone)
  //   + Fifth above lower root          (droneFifthOn)
  //   + Upper root (octave above lower) (droneOctaveOn)
  //   + Upper fifth (octave above fifth)(droneFifthOn && droneOctaveOn)
  const [droneOn, setDroneOn] = useState(false);
  const [droneFifthOn, setDroneFifthOn] = useState<boolean>(() => {
    return localStorage.getItem('stringforge-drone-fifth') === 'true';
  });
  useEffect(() => { localStorage.setItem('stringforge-drone-fifth', String(droneFifthOn)); }, [droneFifthOn]);
  const [droneOctaveOn, setDroneOctaveOn] = useState<boolean>(() => {
    return localStorage.getItem('stringforge-drone-octave') === 'true';
  });
  useEffect(() => { localStorage.setItem('stringforge-drone-octave', String(droneOctaveOn)); }, [droneOctaveOn]);
  const droneLowerRef = useRef<Tone.Oscillator | null>(null);          // lower root (always)
  const droneUpperRef = useRef<Tone.Oscillator | null>(null);          // upper root (octave above)
  const droneFifthRef = useRef<Tone.Oscillator | null>(null);          // fifth above lower root
  const droneFifthUpperRef = useRef<Tone.Oscillator | null>(null);     // octave above the fifth
  const droneUpperGainRef = useRef<Tone.Gain | null>(null);            // gate: +Octave (root upper)
  const droneFifthGainRef = useRef<Tone.Gain | null>(null);            // gate: +5th
  const droneFifthUpperGainRef = useRef<Tone.Gain | null>(null);       // gate: +5th && +Octave
  const droneGainRef = useRef<Tone.Gain | null>(null);
  const droneFilterRef = useRef<Tone.Filter | null>(null);
  const droneReverbRef = useRef<Tone.Reverb | null>(null);
  const droneVibratoRef = useRef<Tone.LFO | null>(null);

  // Theory-mode note duration (persisted separately from compose mode).
  // Compose mode keeps its existing 8th-note default; Theory mode defaults to quarter
  // since scale practice is usually slower and lands cleanly on the beat.
  const NOTE_DURATIONS: Record<string, { tone: string; beats: number }> = {
    '1n':  { tone: '1n',  beats: 4 },
    '2n':  { tone: '2n',  beats: 2 },
    '4n':  { tone: '4n',  beats: 1 },
    '8n':  { tone: '8n',  beats: 0.5 },
    '16n': { tone: '16n', beats: 0.25 },
  };
  const [theoryNoteValue, setTheoryNoteValue] = useState<string>(() => {
    return localStorage.getItem('stringforge-theory-note-value') || '4n';
  });
  useEffect(() => { localStorage.setItem('stringforge-theory-note-value', theoryNoteValue); }, [theoryNoteValue]);

  // Fiddle has no frets — force capo to 0 if it's ever non-zero on fiddle
  // (covers reloads from old localStorage, melody/riff loads, etc.).
  useEffect(() => {
    if (instrument === 'fiddle' && capo !== 0) setCapo(0);
  }, [instrument, capo]);

  // Derived from instrument
  const config = INSTRUMENTS[instrument];
  const NUM_FRETS = config.numFrets;
  const openStrings = config.tunings[tuning] || Object.values(config.tunings)[0];
  const FIFTH_STRING_START = config.fifthString?.startFret ?? 0;
  const STRING_SPACING = instrument === 'fiddle' ? 40 : 35;
  const SVG_HEIGHT = 45 + config.strings * STRING_SPACING + 50;

  // Parse time signature
  const beatsPerMeasure = parseInt(timeSignature.split('/')[0]) || 4;
  const slotsPerMeasure = beatsPerMeasure * 8;

  // Theory notes computation: maps note-name -> semitone interval from root
  // theoryChord (when set) RESTRICTS the visible set to just chord tones, since
  // chords are a "focus of the scale".
  const theoryNotes = useMemo(() => {
    const empty = { activeNotes: new Map<string, number>(), blue: new Set<string>(), rootName: '' };
    if (mode !== 'theory') return empty;
    const rootIdx = NOTE_NAMES.indexOf(theoryKey);
    if (rootIdx < 0) return empty;

    const activeNotes = new Map<string, number>();
    let intervals: number[];
    if (theoryChord !== 'none' && CHORDS[theoryChord]) {
      intervals = CHORDS[theoryChord].intervals;
    } else if (SCALES[theoryScale]) {
      intervals = SCALES[theoryScale].intervals;
    } else {
      intervals = [];
    }
    intervals.forEach(interval => {
      const noteName = NOTE_NAMES[(rootIdx + interval) % 12];
      // Keep the smallest semitone offset for label (so root labels as '1', not '13')
      if (!activeNotes.has(noteName)) activeNotes.set(noteName, interval);
    });

    const blue = new Set<string>();
    if (showBlueNotes) {
      [3, 6, 10].forEach(interval => {
        blue.add(NOTE_NAMES[(rootIdx + interval) % 12]);
      });
    }

    return { activeNotes, blue, rootName: theoryKey };
  }, [mode, theoryKey, theoryScale, theoryChord, showBlueNotes]);

  // Persist mode + theory selections
  useEffect(() => { localStorage.setItem('stringforge-mode', mode); }, [mode]);
  useEffect(() => { localStorage.setItem('stringforge-theory-key', theoryKey); }, [theoryKey]);
  useEffect(() => { localStorage.setItem('stringforge-theory-scale', theoryScale); }, [theoryScale]);
  useEffect(() => { localStorage.setItem('stringforge-theory-chord', theoryChord); }, [theoryChord]);
  useEffect(() => { localStorage.setItem('stringforge-fret-min', String(fretRangeMin)); }, [fretRangeMin]);
  useEffect(() => { localStorage.setItem('stringforge-fret-max', String(fretRangeMax)); }, [fretRangeMax]);
  useEffect(() => { localStorage.setItem('stringforge-theory-direction', theoryDirection); }, [theoryDirection]);
  useEffect(() => { localStorage.setItem('stringforge-theory-start-from-root', String(startFromRoot)); }, [startFromRoot]);

  // Clamp fret range when instrument changes
  useEffect(() => {
    const max = INSTRUMENTS[instrument].numFrets;
    setFretRangeMax(prev => Math.min(prev, max));
    setFretRangeMin(prev => Math.min(prev, max));
  }, [instrument]);

  // Shift fret range when capo changes, preserving the span.
  // Range moves WITH the capo so the practice window slides with you.
  const prevCapoRef = useRef<number>(capo);
  useEffect(() => {
    const prevCapo = prevCapoRef.current;
    const delta = capo - prevCapo;
    prevCapoRef.current = capo;
    if (delta === 0) return;
    const max = INSTRUMENTS[instrument].numFrets;
    setFretRangeMin(prev => {
      const shifted = prev + delta;
      // Floor at capo (can't reach below the capo)
      return Math.max(capo, Math.min(max, shifted));
    });
    setFretRangeMax(prev => {
      const shifted = prev + delta;
      return Math.max(capo, Math.min(max, shifted));
    });
  }, [capo, instrument]);

  // String labels: show open note name for each string
  const stringLabels = openStrings.map((noteStr, idx) => {
    const name = getNoteName(noteStr);
    if (instrument === 'banjo') {
      const ordinals = ['5th', '4th', '3rd', '2nd', '1st'];
      return `${ordinals[idx]} (${name})`;
    }
    return name;
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedNotes(new Set());
        setSelectionAnchor(null);
        setPitchEditId(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedNotes.size > 0) {
        const copied = sequence.filter(n => selectedNotes.has(n.id));
        setClipboard(copied);
        e.preventDefault();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard.length > 0) {
        const pastedNotes = clipboard.map(n => ({
          ...n,
          id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
        }));
        setSequence(prev => {
          if (selectedNotes.size > 0) {
            let lastSelectedIdx = -1;
            prev.forEach((n, i) => {
              if (selectedNotes.has(n.id)) lastSelectedIdx = i;
            });
            if (lastSelectedIdx >= 0) {
              const next = [...prev];
              next.splice(lastSelectedIdx + 1, 0, ...pastedNotes);
              return next;
            }
          }
          return [...prev, ...pastedNotes];
        });
        setSelectedNotes(new Set(pastedNotes.map(n => n.id)));
        setSelectionAnchor(null);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNotes, sequence, clipboard]);

  // Initialize synths
  useEffect(() => {
    synthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.02, decay: 0.3, sustain: 0.2, release: 0.5 },
    }).toDestination();
    tickRef.current = new Tone.Synth({
      oscillator: { type: 'sine' },
      envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.01 },
      volume: -6,
    }).toDestination();
    tockRef.current = new Tone.Synth({
      oscillator: { type: 'sine' },
      envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.01 },
      volume: -4,
    }).toDestination();
    return () => {
      synthRef.current?.dispose();
      tickRef.current?.dispose();
      tockRef.current?.dispose();
    };
  }, []);

  // Load saved riffs
  useEffect(() => {
    const saved = localStorage.getItem('stringforge-riffs');
    if (saved) setSavedRiffs(JSON.parse(saved));
  }, []);

  // Load melody library
  useEffect(() => {
    fetch('/api/library')
      .then(r => r.json())
      .then((data: MelodyEntry[]) => setMelodyLibrary(data))
      .catch(() => setMelodyLibrary([]));
  }, []);

  const refreshLibrary = () => {
    fetch('/api/library')
      .then(r => r.json())
      .then((data: MelodyEntry[]) => setMelodyLibrary(data))
      .catch(() => {});
  };

  const loadMelody = (melody: MelodyEntry) => {
    setTransposeSemitones(0);
    setLoadedMelody(melody);
    if (melody.tuning) setTuning(melody.tuning);
    setCapo(melody.capo || 0);
    setBpm(melody.bpm);
    if (melody.timeSignature) setTimeSignature(melody.timeSignature);
    const newSequence: SequenceNote[] = melody.notes.map((n) => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      note: n.note,
      duration: n.duration,
      beats: n.beats,
      isRest: n.isRest || false,
    }));
    setSequence(newSequence);
    setShowLibrary(false);
    setSelectedNotes(new Set());
    setPitchEditId(null);
    stopPlayback();
  };

  const applyTranspose = (newSemitones: number) => {
    if (!loadedMelody) return;
    const diff = newSemitones - transposeSemitones;
    setTransposeSemitones(newSemitones);
    setSequence(prev => prev.map(n => {
      if (n.isRest) return n;
      return { ...n, note: transposeNoteStr(n.note, diff) };
    }));
  };

  const filteredLibrary = librarySearch.trim() === ''
    ? melodyLibrary
    : melodyLibrary.filter(m =>
        m.name.toLowerCase().includes(librarySearch.toLowerCase())
      );

  const playNoteSound = async (noteStr: string, duration?: string) => {
    await Tone.start();
    synthRef.current?.triggerAttackRelease(noteStr, duration || '8n');
  };

  const highlightNote = (noteStr: string) => {
    setActiveNotes(prev => new Set(prev).add(noteStr));
    setTimeout(() => {
      setActiveNotes(prev => {
        const next = new Set(prev);
        next.delete(noteStr);
        return next;
      });
    }, 300);
  };

  const handleNoteClick = async (stringIdx: number, fret: number, noteStr: string) => {
    await playNoteSound(noteStr);
    highlightNote(noteStr);
    
    if (mode === 'compose') {
      const newNote: SequenceNote = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
        note: noteStr,
        duration: selectedDuration.value,
        beats: selectedDuration.beats,
      };
      if (selectedNotes.size > 0) {
        setSequence(prev => {
          let lastSelectedIdx = -1;
          prev.forEach((n, i) => { if (selectedNotes.has(n.id)) lastSelectedIdx = i; });
          if (lastSelectedIdx >= 0) {
            const next = [...prev];
            next.splice(lastSelectedIdx + 1, 0, newNote);
            return next;
          }
          return [...prev, newNote];
        });
        setSelectedNotes(new Set([newNote.id]));
        setSelectionAnchor(null);
      } else {
        setSequence(prev => [...prev, newNote]);
      }
    }
  };

  const removeNote = (id: string) => {
    setSequence(prev => prev.filter(n => n.id !== id));
    setSelectedNotes(prev => { const next = new Set(prev); next.delete(id); return next; });
    if (pitchEditId === id) setPitchEditId(null);
  };

  const clearSequence = () => {
    setSequence([]);
    setPlayingIndex(-1);
    setSelectedNotes(new Set());
    setPitchEditId(null);
  };

  const convertToRest = (id: string) => {
    setSequence(prev => prev.map(n => {
      if (n.id !== id) return n;
      return { ...n, note: 'REST', isRest: true };
    }));
    setPitchEditId(null);
  };

  const convertFromRest = (id: string) => {
    setSequence(prev => {
      const idx = prev.findIndex(n => n.id === id);
      if (idx === -1) return prev;
      let contextNote = 'G4';
      for (let i = idx - 1; i >= 0; i--) {
        if (!prev[i].isRest) { contextNote = prev[i].note; break; }
      }
      if (contextNote === 'G4') {
        for (let i = idx + 1; i < prev.length; i++) {
          if (!prev[i].isRest) { contextNote = prev[i].note; break; }
        }
      }
      return prev.map(n => {
        if (n.id !== id) return n;
        return { ...n, note: contextNote, isRest: false };
      });
    });
  };

  const shiftPitch = (id: string, semitones: number) => {
    setSequence(prev => prev.map(n => {
      if (n.id !== id || n.isRest) return n;
      const newNote = transposeNoteStr(n.note, semitones);
      return { ...n, note: newNote };
    }));
  };

  // Standalone metronome
  useEffect(() => {
    if (metronomeState === 'on') {
      const startMetronome = async () => {
        await Tone.start();
        const beatDurationMs = (60 / bpm) * 1000;
        standaloneMetronomeBeatRef.current = 0;
        tockRef.current?.triggerAttackRelease('G6', '32n');
        standaloneMetronomeBeatRef.current = 1;
        standaloneMetronomeRef.current = setInterval(() => {
          const beat = standaloneMetronomeBeatRef.current % beatsPerMeasure;
          if (beat === 0) {
            tockRef.current?.triggerAttackRelease('G6', '32n');
          } else {
            tickRef.current?.triggerAttackRelease('C6', '32n');
          }
          standaloneMetronomeBeatRef.current++;
        }, beatDurationMs);
      };
      startMetronome();
    } else {
      if (standaloneMetronomeRef.current) {
        clearInterval(standaloneMetronomeRef.current);
        standaloneMetronomeRef.current = null;
      }
    }
    return () => {
      if (standaloneMetronomeRef.current) {
        clearInterval(standaloneMetronomeRef.current);
        standaloneMetronomeRef.current = null;
      }
    };
  }, [metronomeState, bpm, beatsPerMeasure]);

  const cycleMetronomeState = () => {
    setMetronomeState(prev => {
      if (prev === 'off') return 'with-melody';
      if (prev === 'with-melody') return 'on';
      return 'off';
    });
  };

  const metronomeLabel = metronomeState === 'off' ? '🥁 Metro OFF' 
    : metronomeState === 'with-melody' ? '🥁 Metro w/Melody' 
    : '🥁 Metro ON';

  const playTick = () => {
    tickRef.current?.triggerAttackRelease('C6', '32n');
  };
  const playTock = () => {
    tockRef.current?.triggerAttackRelease('G6', '32n');
  };

  const playCountIn = useCallback(async () => {
    const beatDurationMs = (60 / bpm) * 1000;
    const beats = beatsPerMeasure;

    if (doubleCountIn) {
      for (let b = 0; b < beats; b++) {
        if (stopRef.current) return;
        if (beatsPerMeasure === 4 && (b === 0 || b === 2)) {
          if (b === 0) playTock(); else playTick();
        } else if (beatsPerMeasure !== 4 && b === 0) {
          playTock();
        }
        await new Promise(resolve => setTimeout(resolve, beatDurationMs));
      }
      for (let b = 0; b < beats; b++) {
        if (stopRef.current) return;
        if (b === 0) playTock(); else playTick();
        await new Promise(resolve => setTimeout(resolve, beatDurationMs));
      }
    } else {
      for (let b = 0; b < beats; b++) {
        if (stopRef.current) return;
        if (b === 0) playTock(); else playTick();
        await new Promise(resolve => setTimeout(resolve, beatDurationMs));
      }
    }
  }, [bpm, beatsPerMeasure, doubleCountIn]);

  const playSequence = useCallback(async (notesToPlay?: SequenceNote[]) => {
    const playNotes = notesToPlay || (selectedNotes.size > 0 ? sequence.filter(n => selectedNotes.has(n.id)) : sequence);
    if (playNotes.length === 0) return;
    await Tone.start();
    setIsPlaying(true);
    stopRef.current = false;

    const play = async () => {
      await playCountIn();
      if (stopRef.current) { setIsPlaying(false); setPlayingIndex(-1); playingIndexRef.current = -1; return; }

      let isFirstLoop = true;
      do {
        if (!isFirstLoop && loopCountIn) {
          await playCountIn();
          if (stopRef.current) { setIsPlaying(false); setPlayingIndex(-1); playingIndexRef.current = -1; return; }
        }
        isFirstLoop = false;

        let beatAccumulator = 0;

        for (let i = 0; i < playNotes.length; i++) {
          if (stopRef.current) {
            setIsPlaying(false);
            setPlayingIndex(-1);
            playingIndexRef.current = -1;
            return;
          }
          const note = playNotes[i];
          const fullIdx = sequence.indexOf(note);
          setPlayingIndex(fullIdx);
          playingIndexRef.current = fullIdx;

          if (metronomeState === 'with-melody') {
            const beatPos = beatAccumulator % beatsPerMeasure;
            const rounded = Math.round(beatPos);
            if (Math.abs(beatPos - rounded) < 0.01) {
              const beatInMeasure = rounded % beatsPerMeasure;
              if (beatInMeasure === 0) playTock(); else playTick();
            }
          }

          if (!note.isRest) {
            highlightNote(note.note);
            synthRef.current?.triggerAttackRelease(note.note, note.duration);
          }
          
          const beatDurationMs = (60 / bpm) * 1000;
          const noteDurationMs = note.beats * beatDurationMs;

          let remainingMs = noteDurationMs;
          let accum = beatAccumulator;
          while (remainingMs > 1) {
            if (stopRef.current) { setIsPlaying(false); setPlayingIndex(-1); playingIndexRef.current = -1; return; }
            const nextBeat = Math.ceil(accum + 0.001);
            const msToNextBeat = (nextBeat - accum) * beatDurationMs;
            
            if (msToNextBeat < remainingMs - 0.5) {
              await new Promise(resolve => setTimeout(resolve, msToNextBeat));
              remainingMs -= msToNextBeat;
              accum = nextBeat;
              const beatInMeasure = Math.round(accum) % beatsPerMeasure;
              if (metronomeState === 'with-melody') {
                if (beatInMeasure === 0) playTock(); else playTick();
              }
            } else {
              await new Promise(resolve => setTimeout(resolve, remainingMs));
              accum += remainingMs / beatDurationMs;
              remainingMs = 0;
            }
          }
          beatAccumulator = accum;
        }
      } while (isLooping && !stopRef.current);
      
      setIsPlaying(false);
      setPlayingIndex(-1);
      playingIndexRef.current = -1;
    };
    
    play();
  }, [sequence, bpm, isLooping, selectedNotes, metronomeState, beatsPerMeasure, loopCountIn, playCountIn]);

  // Build a sorted (low->high), de-duplicated list of in-range, in-scale notes
  // for the current Theory selection.
  const getTheoryNoteSequence = useCallback((): string[] => {
    if (mode !== 'theory') return [];
    const cfg = INSTRUMENTS[instrument];
    const candidates: { midi: number; note: string }[] = [];
    for (let stringIdx = 0; stringIdx < cfg.strings; stringIdx++) {
      const isBanjoFifth = instrument === 'banjo' && stringIdx === 0;
      const minFret = isBanjoFifth
        ? Math.max(fretRangeMin, FIFTH_STRING_START, capo)
        : Math.max(fretRangeMin, capo);
      const maxFret = fretRangeMax;
      for (let fret = minFret; fret <= maxFret; fret++) {
        if (fret < 0 || fret > cfg.numFrets) continue;
        let note: string;
        if (fret === 0) {
          note = openStrings[stringIdx];
        } else {
          note = getNoteAt(stringIdx, fret);
        }
        if (!isTheoryNoteVisible(note)) continue;
        candidates.push({ midi: noteToMidi(note), note });
      }
    }
    // Sort ascending, dedupe by midi
    candidates.sort((a, b) => a.midi - b.midi);
    const seen = new Set<number>();
    const result: string[] = [];
    for (const c of candidates) {
      if (seen.has(c.midi)) continue;
      seen.add(c.midi);
      result.push(c.note);
    }
    return result;
  }, [mode, instrument, openStrings, capo, fretRangeMin, fretRangeMax, theoryNotes, FIFTH_STRING_START]);

  const playTheoryScale = useCallback(() => {
    let ascNotes = getTheoryNoteSequence();
    if (ascNotes.length === 0) return;

    // "Start from Root": if we have >= 2 roots in range, trim to lowest-root..highest-root.
    // Otherwise fall back to everything in range.
    if (startFromRoot) {
      const rootName = theoryKey;
      const rootIdxs: number[] = [];
      ascNotes.forEach((n, i) => {
        if (getNoteName(n) === rootName) rootIdxs.push(i);
      });
      if (rootIdxs.length >= 2) {
        const first = rootIdxs[0];
        const last = rootIdxs[rootIdxs.length - 1];
        ascNotes = ascNotes.slice(first, last + 1);
      }
    }

    let ordered: string[];
    if (theoryDirection === 'up') ordered = ascNotes;
    else if (theoryDirection === 'down') ordered = [...ascNotes].reverse();
    else {
      // both: up then down, don't repeat the top note
      const down = [...ascNotes].reverse().slice(1);
      ordered = [...ascNotes, ...down];
    }
    const dur = NOTE_DURATIONS[theoryNoteValue] || NOTE_DURATIONS['4n'];
    const seqNotes: SequenceNote[] = ordered.map((n, i) => ({
      id: `theory-${i}-${Date.now()}`,
      note: n,
      duration: dur.tone,
      beats: dur.beats,
    }));
    playSequence(seqNotes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTheoryNoteSequence, theoryDirection, startFromRoot, theoryKey, theoryNoteValue, playSequence]);

  // --- Drone: build the audio graph once, tear it down on unmount.
  // Lower root -> master gain (always).
  // Each optional voice routes through its own gain gate -> master gain, so toggles fade
  // in/out smoothly without rebuilding the graph.
  const startDroneGraph = useCallback(async () => {
    if (droneGainRef.current) return; // already built
    await Tone.start();
    const reverb = new Tone.Reverb({ decay: 5, wet: 0.45 }).toDestination();
    await reverb.generate();
    const filter = new Tone.Filter({ frequency: 900, type: 'lowpass', Q: 0.7 }).connect(reverb);
    const gain = new Tone.Gain(0).connect(filter); // master, fades in/out

    // Always-on lower root: cello-foundation body, sits below the lowest playable root.
    const lower = new Tone.Oscillator({ type: 'sawtooth', volume: -12, frequency: 'D2' }).connect(gain);

    // Upper root (octave above lower) — gated by +Octave.
    const upperGate = new Tone.Gain(0).connect(gain);
    const upper = new Tone.Oscillator({ type: 'sawtooth', volume: -10, frequency: 'D3' }).connect(upperGate);

    // Fifth above lower root — gated by +5th.
    const fifthGate = new Tone.Gain(0).connect(gain);
    const fifth = new Tone.Oscillator({ type: 'sawtooth', volume: -13, frequency: 'A2' }).connect(fifthGate);

    // Upper fifth (octave above the fifth) — gated by +5th && +Octave.
    const fifthUpperGate = new Tone.Gain(0).connect(gain);
    const fifthUpper = new Tone.Oscillator({ type: 'sawtooth', volume: -13, frequency: 'A3' }).connect(fifthUpperGate);

    // Subtle vibrato on the upper root so the rich mode doesn't sound dead.
    const vibrato = new Tone.LFO({ frequency: 4.8, min: -6, max: 6 }).start();
    vibrato.connect(upper.detune);

    lower.start(); upper.start(); fifth.start(); fifthUpper.start();

    droneReverbRef.current = reverb;
    droneFilterRef.current = filter;
    droneGainRef.current = gain;
    droneLowerRef.current = lower;
    droneUpperRef.current = upper;
    droneFifthRef.current = fifth;
    droneFifthUpperRef.current = fifthUpper;
    droneUpperGainRef.current = upperGate;
    droneFifthGainRef.current = fifthGate;
    droneFifthUpperGainRef.current = fifthUpperGate;
    droneVibratoRef.current = vibrato;
  }, []);

  // Compute the lower-root drone pitch:
  // "One octave below the lowest playable root on the fiddle in the current tuning."
  // Equivalent shortcut: floor = lowest open string − 12, then snap UP to the key's pitch
  // class. (Because any sensible first-position fingering of the root lands within
  // 12 semitones of the lowest open string, the lowest playable root is always at
  // floor + 12; dropping one octave puts us back at the snap-up point.)
  const droneLowerMidi = useMemo(() => {
    const openMidis = openStrings.map(noteToMidi);
    if (openMidis.length === 0) return noteToMidi(`${theoryKey}2`);
    const lowestOpen = Math.min(...openMidis);
    const floor = lowestOpen - 12;
    const rootPc = noteToMidi(`${theoryKey}0`) % 12;
    let midi = floor;
    while ((midi % 12 + 12) % 12 !== rootPc) midi++;
    return midi;
  }, [openStrings, theoryKey]);

  // Retune all voices when the target pitches change (key or tuning).
  useEffect(() => {
    if (!droneLowerRef.current || !droneUpperRef.current || !droneFifthRef.current || !droneFifthUpperRef.current) return;
    droneLowerRef.current.frequency.rampTo(midiToNote(droneLowerMidi), 0.05);
    droneUpperRef.current.frequency.rampTo(midiToNote(droneLowerMidi + 12), 0.05);
    droneFifthRef.current.frequency.rampTo(midiToNote(droneLowerMidi + 7), 0.05);
    droneFifthUpperRef.current.frequency.rampTo(midiToNote(droneLowerMidi + 19), 0.05);
  }, [droneLowerMidi]);

  // Toggle gates: +Octave (root upper), +5th (fifth), +5th && +Octave (fifth upper).
  useEffect(() => {
    droneUpperGainRef.current?.gain.rampTo(droneOctaveOn ? 1 : 0, 0.2);
    droneFifthGainRef.current?.gain.rampTo(droneFifthOn ? 1 : 0, 0.2);
    droneFifthUpperGainRef.current?.gain.rampTo((droneFifthOn && droneOctaveOn) ? 1 : 0, 0.2);
  }, [droneOctaveOn, droneFifthOn]);

  // Drone on/off with fades. On first start, apply the current toggle states to the
  // freshly-built gates before fading in the master gain.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (droneOn) {
        await startDroneGraph();
        if (cancelled) return;
        droneUpperGainRef.current?.gain.rampTo(droneOctaveOn ? 1 : 0, 0.05);
        droneFifthGainRef.current?.gain.rampTo(droneFifthOn ? 1 : 0, 0.05);
        droneFifthUpperGainRef.current?.gain.rampTo((droneFifthOn && droneOctaveOn) ? 1 : 0, 0.05);
        droneGainRef.current?.gain.rampTo(0.35, 0.25); // 250ms fade-in
      } else if (droneGainRef.current) {
        droneGainRef.current.gain.rampTo(0, 0.2); // 200ms fade-out
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droneOn, droneFifthOn, droneOctaveOn, startDroneGraph]);

  // Auto-stop drone when leaving Theory mode.
  useEffect(() => {
    if (mode !== 'theory' && droneOn) setDroneOn(false);
  }, [mode, droneOn]);

  // Cleanup on unmount.
  useEffect(() => () => {
    droneLowerRef.current?.stop(); droneLowerRef.current?.dispose();
    droneUpperRef.current?.stop(); droneUpperRef.current?.dispose();
    droneFifthRef.current?.stop(); droneFifthRef.current?.dispose();
    droneFifthUpperRef.current?.stop(); droneFifthUpperRef.current?.dispose();
    droneUpperGainRef.current?.dispose();
    droneFifthGainRef.current?.dispose();
    droneFifthUpperGainRef.current?.dispose();
    droneVibratoRef.current?.stop(); droneVibratoRef.current?.dispose();
    droneGainRef.current?.dispose();
    droneFilterRef.current?.dispose();
    droneReverbRef.current?.dispose();
  }, []);

  const stopPlayback = () => {
    stopRef.current = true;
    setIsPlaying(false);
    setPlayingIndex(-1);
    playingIndexRef.current = -1;
  };

  const saveRiff = () => {
    if (!riffName.trim()) return;
    const riff: SavedRiff = {
      name: riffName.trim(),
      instrument,
      tuning,
      capo,
      bpm,
      timeSignature,
      notes: sequence,
      savedAt: new Date().toISOString(),
    };
    const updated = [...savedRiffs, riff];
    setSavedRiffs(updated);
    localStorage.setItem('stringforge-riffs', JSON.stringify(updated));
    setRiffName('');
    setShowSaveDialog(false);
  };

  const loadRiff = (riff: SavedRiff) => {
    if (riff.instrument) {
      setInstrument(riff.instrument as InstrumentType);
    }
    setTuning(riff.tuning);
    setCapo(riff.capo || 0);
    setBpm(riff.bpm);
    if (riff.timeSignature) setTimeSignature(riff.timeSignature);
    setSequence(riff.notes);
    setSelectedNotes(new Set());
    setPitchEditId(null);
    stopPlayback();
  };

  const deleteRiff = (index: number) => {
    const rName = savedRiffs[index]?.name || 'this riff';
    if (!window.confirm(`Delete "${rName}" from Saved Riffs?`)) return;
    const updated = savedRiffs.filter((_, i) => i !== index);
    setSavedRiffs(updated);
    localStorage.setItem('stringforge-riffs', JSON.stringify(updated));
  };

  const transferToLibrary = async (riff: SavedRiff) => {
    const key = prompt(`What key is "${riff.name}" in?`, 'G');
    if (key === null) return;
    const trimmedKey = key.trim();
    if (!NOTE_NAMES_ALL.includes(trimmedKey)) {
      alert(`"${trimmedKey}" is not a valid key. Use: C, C#, D, D#, E, F, F#, G, G#, A, A#, B`);
      return;
    }
    const melodyEntry: MelodyEntry = {
      name: riff.name,
      key: trimmedKey,
      tuning: riff.tuning,
      capo: riff.capo || 0,
      bpm: riff.bpm,
      timeSignature: riff.timeSignature || timeSignature,
      transcribedAt: new Date().toISOString(),
      notes: riff.notes.map(n => ({
        note: n.note,
        duration: n.duration,
        beats: n.beats,
        ...(n.isRest ? { isRest: true } : {}),
      })),
    };
    try {
      const res = await fetch('/api/library', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(melodyEntry),
      });
      if (res.status === 409) {
        alert(`"${riff.name}" already exists in the Melody Library. Please rename the riff first.`);
        return;
      }
      if (!res.ok) throw new Error('Save failed');
      refreshLibrary();
    } catch (err) {
      alert('Failed to save to library');
    }
  };

  const deleteFromLibrary = async (name: string) => {
    if (!window.confirm(`Delete "${name}" from the Melody Library?`)) return;
    try {
      const res = await fetch(`/api/library/${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      refreshLibrary();
    } catch {
      alert('Failed to delete from library');
    }
  };

  const addRest = () => {
    const rest: SequenceNote = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      note: 'REST',
      duration: selectedDuration.value,
      beats: selectedDuration.beats,
      isRest: true,
    };
    if (selectedNotes.size > 0) {
      setSequence(prev => {
        let lastSelectedIdx = -1;
        prev.forEach((n, i) => { if (selectedNotes.has(n.id)) lastSelectedIdx = i; });
        if (lastSelectedIdx >= 0) {
          const next = [...prev];
          next.splice(lastSelectedIdx + 1, 0, rest);
          return next;
        }
        return [...prev, rest];
      });
      setSelectedNotes(new Set([rest.id]));
      setSelectionAnchor(null);
    } else {
      setSequence(prev => [...prev, rest]);
    }
  };

  const updateNoteDuration = (id: string, dur: typeof DURATIONS[0]) => {
    setSequence(prev => prev.map(n => 
      n.id === id ? { ...n, duration: dur.value, beats: dur.beats } : n
    ));
  };

  // Effective open string notes with capo applied
  const getCapoOpenNote = (stringIdx: number): string => {
    const openMidi = noteToMidi(openStrings[stringIdx]);
    if (instrument === 'banjo' && stringIdx === 0) {
      const effectiveCapo = Math.max(0, capo - FIFTH_STRING_START);
      return midiToNote(openMidi + (capo >= FIFTH_STRING_START ? capo - FIFTH_STRING_START : 0));
    }
    return midiToNote(openMidi + capo);
  };

  const getNoteAt = (stringIdx: number, fret: number): string => {
    const openMidi = noteToMidi(openStrings[stringIdx]);
    if (instrument === 'banjo' && stringIdx === 0) {
      return midiToNote(openMidi + (fret - FIFTH_STRING_START));
    }
    return midiToNote(openMidi + fret);
  };

  // --- Build measure grid layout ---
  const buildMeasureGrid = () => {
    const measures: { notes: { note: SequenceNote; slots: number; seqIdx: number }[]; filledSlots: number }[] = [];
    let currentMeasure: { notes: { note: SequenceNote; slots: number; seqIdx: number }[]; filledSlots: number } = { notes: [], filledSlots: 0 };
    
    sequence.forEach((note, idx) => {
      const slots = beatsToSlots(note.beats);
      let remaining = slots;
      
      while (remaining > 0) {
        const availableSlots = slotsPerMeasure - currentMeasure.filledSlots;
        if (availableSlots <= 0) {
          measures.push(currentMeasure);
          currentMeasure = { notes: [], filledSlots: 0 };
          continue;
        }
        const useSlots = Math.min(remaining, availableSlots);
        currentMeasure.notes.push({ note, slots: useSlots, seqIdx: idx });
        currentMeasure.filledSlots += useSlots;
        remaining -= useSlots;
        
        if (currentMeasure.filledSlots >= slotsPerMeasure) {
          measures.push(currentMeasure);
          currentMeasure = { notes: [], filledSlots: 0 };
        }
      }
    });
    
    if (currentMeasure.notes.length > 0) {
      measures.push(currentMeasure);
    }
    
    return measures;
  };

  const buildRows = () => {
    const measures = buildMeasureGrid();
    const rows: typeof measures[] = [];
    for (let i = 0; i < measures.length; i += measuresPerRow) {
      rows.push(measures.slice(i, i + measuresPerRow));
    }
    return rows;
  };

  const styles = {
    container: { padding: '15px', fontFamily: 'Arial, sans-serif', background: '#1a1a2e', color: '#eee', minHeight: '100vh' } as React.CSSProperties,
    header: { textAlign: 'center' as const, color: '#e94560', margin: '0 0 3px 0', fontSize: '24px' },
    subtitle: { textAlign: 'center' as const, color: '#888', margin: '0 0 15px 0', fontSize: '13px' },
    controlBar: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', flexWrap: 'wrap' as const, marginBottom: '10px', padding: '10px', background: '#16213e', borderRadius: '8px' },
    select: { padding: '6px 12px', fontSize: '14px', background: '#0f3460', color: '#eee', border: '1px solid #e94560', borderRadius: '4px', cursor: 'pointer' },
    btn: (active?: boolean) => ({ padding: '8px 16px', fontSize: '14px', background: active ? '#e94560' : '#0f3460', color: '#eee', border: '1px solid #e94560', borderRadius: '4px', cursor: 'pointer', fontWeight: active ? 'bold' as const : 'normal' as const }),
    btnSmall: { padding: '4px 8px', fontSize: '12px', background: '#0f3460', color: '#eee', border: '1px solid #555', borderRadius: '3px', cursor: 'pointer' },
    btnDanger: { padding: '4px 8px', fontSize: '12px', background: '#8b0000', color: '#eee', border: '1px solid #e94560', borderRadius: '3px', cursor: 'pointer' },
    sequenceContainer: { margin: '15px 0', padding: '10px', background: '#16213e', borderRadius: '8px', minHeight: '80px' },
    input: { padding: '6px 12px', fontSize: '14px', background: '#0f3460', color: '#eee', border: '1px solid #555', borderRadius: '4px', width: '200px' },
    savedRiff: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', margin: '3px', background: '#0f3460', border: '1px solid #333', borderRadius: '6px', cursor: 'pointer' },
  };

  const SLOT_WIDTH = 20;
  const MEASURE_GAP = 3;
  const BAR_LINE_WIDTH = 2;

  // In theory mode, a note is in the visible/active set if it's a chord/scale tone
  // (or a blue note when enabled). Notes outside the fret range or outside the
  // active set are hidden entirely.
  const isFretInRange = (fret: number): boolean => {
    if (mode !== 'theory') return true;
    return fret >= fretRangeMin && fret <= fretRangeMax;
  };

  // Returns fill color for theory-active notes or null when note should be
  // hidden (in theory mode) or use normal rendering (other modes).
  const getTheoryFill = (noteStr: string): string | null => {
    if (mode !== 'theory') return null;
    const noteName = getNoteName(noteStr);
    const interval = theoryNotes.activeNotes.get(noteName);
    if (interval === undefined) {
      if (theoryNotes.blue.has(noteName)) return '#4B9CD3';   // blue notes: blue
      return null;
    }
    if (interval === 0) return '#e94560';         // root: red
    if (interval === 7) return '#f08a9a';         // 5th: light red
    return '#8b5cf6';                             // scale/chord tones: purple
  };

  // Should this note be rendered at all in theory mode?
  const isTheoryNoteVisible = (noteStr: string): boolean => {
    if (mode !== 'theory') return true;
    const noteName = getNoteName(noteStr);
    return theoryNotes.activeNotes.has(noteName) || theoryNotes.blue.has(noteName);
  };

  const getTheoryStroke = (noteStr: string): string => {
    const fill = getTheoryFill(noteStr);
    if (fill) return fill;
    if (mode === 'theory') return '#333';
    return mode === 'compose' ? '#00ff88' : '#0f3460';
  };

  const getTheoryTextFill = (noteStr: string, isActive: boolean): string => {
    if (isActive) return '#fff';
    const fill = getTheoryFill(noteStr);
    if (fill) return '#fff';
    if (mode === 'theory') return '#555';
    return '#aaa';
  };

  const getTheoryCircleFill = (noteStr: string, isActive: boolean): string => {
    if (isActive) return '#22c55e';
    const fill = getTheoryFill(noteStr);
    if (fill) return fill;
    return '#16213e';
  };

  // Label for a note in theory mode: "G/1" (root) or "B/3". Falls back to note name.
  const getTheoryLabel = (noteStr: string): string => {
    const noteName = getNoteName(noteStr);
    if (mode !== 'theory') return noteName;
    const interval = theoryNotes.activeNotes.get(noteName);
    if (interval === undefined) return noteName;
    return `${noteName}/${INTERVAL_LABELS[interval] || '?'}`;
  };

  // Fiddle position labels
  const getFiddlePositionLabel = (fret: number): string | null => {
    if (fret >= 1 && fret <= 4) return fret === 1 ? '1st pos' : null;
    if (fret >= 5 && fret <= 7) return fret === 5 ? '2nd pos' : null;
    if (fret >= 8 && fret <= 11) return fret === 8 ? '3rd pos' : null;
    return null;
  };

  return (
    <div style={styles.container}>
      {/* Help icon — absolute-positioned top-right so it doesn't disrupt the
          centered title block (Story: user guide + ❓ icon, 2026-10-29). */}
      <div style={{ position: 'relative' }}>
        <h1 style={styles.header}>🎵 StringForge</h1>
        <p style={styles.subtitle}>{config.name} Composer & Practice Tool</p>
        <button
          onClick={() => setShowGuide(true)}
          title="Open user guide"
          aria-label="Open user guide"
          style={{
            position: 'absolute',
            top: '0',
            right: '8px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: '#0f3460',
            color: '#eee',
            border: '1px solid #e94560',
            fontSize: '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >❓</button>
      </div>

      {showGuide && <UserGuideModal onClose={() => setShowGuide(false)} />}

      {/* Control Bar */}
      <div style={styles.controlBar}>
        {/* Mode selector — leftmost, segmented pill */}
        <div>
          <label style={{ fontSize: '12px', color: '#888' }}>Mode</label><br/>
          <div style={{ display: 'inline-flex', background: '#0f3460', border: '1px solid #e94560', borderRadius: '20px', padding: '2px', overflow: 'hidden' }}>
            {([
              { value: 'compose' as const, label: '✏️ Compose' },
              { value: 'play'    as const, label: '▶️ Play' },
              { value: 'theory'  as const, label: '🎓 Theory' },
            ]).map(opt => (
              <button
                key={opt.value}
                onClick={() => setMode(opt.value)}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  background: mode === opt.value ? '#e94560' : 'transparent',
                  color: '#eee',
                  border: 'none',
                  borderRadius: '18px',
                  cursor: 'pointer',
                  fontWeight: mode === opt.value ? 'bold' : 'normal',
                  transition: 'background 0.15s',
                }}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ fontSize: '12px', color: '#888' }}>Instrument</label><br/>
          <select value={instrument} onChange={(e) => {
            const inst = e.target.value as InstrumentType;
            setInstrument(inst);
            const cfg = INSTRUMENTS[inst];
            setTuning(cfg.defaultTuning);
            setCapo(0);
          }} style={styles.select}>
            {Object.entries(INSTRUMENTS).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '12px', color: '#888' }}>Tuning</label><br/>
          <select value={tuning} onChange={(e) => setTuning(e.target.value)} style={styles.select}>
            {Object.keys(config.tunings).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {instrument !== 'fiddle' && (
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Capo</label><br/>
            <select value={capo} onChange={(e) => setCapo(parseInt(e.target.value))} style={styles.select}>
              <option value={0}>No Capo</option>
              <option value={1}>Capo 1</option>
              <option value={2}>Capo 2</option>
              <option value={3}>Capo 3</option>
              <option value={4}>Capo 4</option>
              <option value={5}>Capo 5</option>
            </select>
          </div>
        )}

        <div>
          <label style={{ fontSize: '12px', color: '#888' }}>Time Sig</label><br/>
          <select value={timeSignature} onChange={(e) => setTimeSignature(e.target.value)} style={styles.select}>
            <option value="2/4">2/4</option>
            <option value="3/4">3/4</option>
            <option value="4/4">4/4</option>
          </select>
        </div>

        {mode === 'compose' && (
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Note Duration</label><br/>
            <select
              value={selectedDuration.value}
              onChange={(e) => {
                const dur = DURATIONS.find(d => d.value === e.target.value);
                if (dur) setSelectedDuration(dur);
              }}
              style={styles.select}
            >
              {DURATIONS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
        )}

        {mode === 'compose' && (
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Rest</label><br/>
            <button style={styles.btn()} onClick={addRest} title="Add a silent rest">
              𝄾 Rest
            </button>
          </div>
        )}

        <div>
          <label style={{ fontSize: '12px', color: '#888' }}>BPM: {bpm}</label><br/>
          <input 
            type="range" min="40" max="220" value={bpm} 
            onChange={(e) => setBpm(parseInt(e.target.value))}
            style={{ width: '120px', accentColor: '#e94560' }}
          />
        </div>
      </div>

      {/* Theory Panel */}
      {mode === 'theory' && (
        <div style={{ padding: '10px', margin: '8px 0', background: '#16213e', borderRadius: '8px', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Key</label><br/>
            <select value={theoryKey} onChange={e => setTheoryKey(e.target.value)} style={styles.select}>
              {NOTE_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Scale</label><br/>
            <select value={theoryScale} onChange={e => setTheoryScale(e.target.value)} style={styles.select}>
              {Object.entries(SCALES).map(([key, s]) => (
                <option key={key} value={key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Chord (focus)</label><br/>
            <select value={theoryChord} onChange={e => setTheoryChord(e.target.value)} style={styles.select}>
              <option value="none">None (full scale)</option>
              {Object.entries(CHORDS).map(([key, c]) => (
                <option key={key} value={key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: '220px' }}>
            <label style={{ fontSize: '12px', color: '#888' }}>
              Fret Range: {fretRangeMin} – {fretRangeMax}{capo > 0 ? ` (capo ${capo})` : ''}
            </label><br/>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input type="range" min={capo} max={NUM_FRETS}
                value={Math.max(fretRangeMin, capo)}
                onChange={e => {
                  const v = parseInt(e.target.value);
                  setFretRangeMin(Math.min(v, fretRangeMax));
                }}
                style={{ width: '100px', accentColor: '#e94560' }}
              />
              <input type="range" min={capo} max={NUM_FRETS}
                value={Math.max(fretRangeMax, capo)}
                onChange={e => {
                  const v = parseInt(e.target.value);
                  setFretRangeMax(Math.max(v, Math.max(fretRangeMin, capo)));
                }}
                style={{ width: '100px', accentColor: '#e94560' }}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Direction</label><br/>
            <select value={theoryDirection} onChange={e => setTheoryDirection(e.target.value as 'up' | 'down' | 'both')} style={styles.select}>
              <option value="up">↑ Up</option>
              <option value="down">↓ Down</option>
              <option value="both">↕ Up + Down</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#888' }}>Note</label><br/>
            <select value={theoryNoteValue} onChange={e => setTheoryNoteValue(e.target.value)} style={styles.select}>
              <option value="1n">○ Whole</option>
              <option value="2n">◗ Half</option>
              <option value="4n">♩ Quarter</option>
              <option value="8n">♪ Eighth</option>
              <option value="16n">♬ 16th</option>
            </select>
          </div>
          <label style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="checkbox" checked={startFromRoot} onChange={e => setStartFromRoot(e.target.checked)} />
            🎯 Start from Root
          </label>
          <label style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="checkbox" checked={showBlueNotes} onChange={e => setShowBlueNotes(e.target.checked)} />
            Blue Notes
          </label>
          <div style={{ fontSize: '11px', color: '#666' }}>
            <span style={{ color: '#e94560' }}>● Root</span> · <span style={{ color: '#f08a9a' }}>● 5th</span> · <span style={{ color: '#8b5cf6' }}>● Scale</span>{showBlueNotes && <> · <span style={{ color: '#4B9CD3' }}>● Blue</span></>}
          </div>
        </div>
      )}

      {/* Fretboard */}
      <div style={{ overflowX: 'auto', padding: '5px 0' }}>
        {(() => {
          const fullWidth = Math.max(900, (NUM_FRETS + 1) * 52 + 80);
          // Theory mode: crop to the selected fret range and scale up
          let viewBox = `0 0 ${fullWidth} ${SVG_HEIGHT}`;
          let displayWidth: number | string = fullWidth;
          let displayHeight: number | string = SVG_HEIGHT;
          if (mode === 'theory') {
            // Left edge: leave room for the open-note circles (radius 12).
            // When capo===0 & fretRangeMin===0: open notes at cx=65, need vbStartX <= 53.
            // When capo>0 & fretRangeMin===capo: open notes at cx = 80 + capo*52 - 26,
            //   so vbStartX must be <= 80 + capo*52 - 38 to leave 12px for the circle.
            //   Pad another 8px so the circle isn't kissing the edge.
            const showingOpenAtCapo = fretRangeMin === capo && capo > 0;
            const showingOpenZero = fretRangeMin === 0 && capo === 0;
            const vbStartX = showingOpenZero
              ? 0
              : showingOpenAtCapo
                ? Math.max(0, 80 + capo * 52 - 46)
                : Math.max(0, 78 + fretRangeMin * 52 - 28);
            // Right edge: hug the last in-range fret tightly; no need for extra fret-number padding.
            const vbEndX = 80 + fretRangeMax * 52 + 4;
            const vbWidth = Math.max(200, vbEndX - vbStartX);
            viewBox = `${vbStartX} 0 ${vbWidth} ${SVG_HEIGHT}`;
            // Scale up: 5-fret window should comfortably fill ~900px
            const scale = Math.min(2.2, 900 / vbWidth);
            displayWidth = Math.round(vbWidth * scale);
            displayHeight = Math.round(SVG_HEIGHT * scale);
          }
          return (
        <svg 
          width={displayWidth}
          height={displayHeight}
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', margin: '0 auto' }}
        >
          {/* Nut */}
          {capo === 0 ? (
            <rect x={75} y={30} width={4} height={config.strings * STRING_SPACING + 10} fill="#f5f5dc" rx={2} />
          ) : (
            <rect x={78 + capo * 52 - 2} y={28} width={6} height={config.strings * STRING_SPACING + 14} fill="#e94560" rx={2} opacity={0.9} />
          )}
          
          {/* Fret dots */}
          {(config.fretDots || []).map(fret => {
            if (fret > NUM_FRETS || fret <= capo) return null;
            const x = 80 + fret * 52 - 26;
            const midY = 45 + (config.strings - 1) * STRING_SPACING / 2;
            if (fret === 12) return (
              <g key={fret}>
                <circle cx={x} cy={midY - 25} r={5} fill="#333" />
                <circle cx={x} cy={midY + 25} r={5} fill="#333" />
              </g>
            );
            return <circle key={fret} cx={x} cy={midY} r={5} fill="#333" />;
          })}
          
          {/* Fret wires / position markers */}
          {Array.from({ length: NUM_FRETS }, (_, i) => i + 1).map(fret => {
            if (fret <= capo) return null;
            const x = 78 + fret * 52;
            if (config.fretless) {
              // Fiddle: dashed position markers
              return <line key={fret} x1={x + 1} y1={30} x2={x + 1} y2={30 + config.strings * STRING_SPACING}
                stroke="#555" strokeWidth={1} strokeDasharray="4,4" />;
            }
            return <rect key={fret} x={x} y={30} width={2} height={config.strings * STRING_SPACING + 10} fill="#888" />;
          })}
          
          {/* Greyed out area behind capo */}
          {capo > 0 && (
            <rect x={60} y={28} width={18 + capo * 52 - 2} height={config.strings * STRING_SPACING + 14} fill="#0a0a1a" opacity={0.7} rx={4} />
          )}
          {capo > 0 && (
            <rect x={78 + capo * 52 - 2} y={28} width={6} height={config.strings * STRING_SPACING + 14} fill="#e94560" rx={2} opacity={0.9} />
          )}

          {/* Fiddle position labels */}
          {config.fretless && Array.from({ length: NUM_FRETS }, (_, i) => i + 1).map(fret => {
            const label = getFiddlePositionLabel(fret);
            if (!label || fret <= capo) return null;
            const x = 80 + fret * 52 - 26;
            return <text key={`pos-${fret}`} x={x} y={22} textAnchor="middle" fill="#666" fontSize="9">{label}</text>;
          })}
          
          {Array.from({ length: config.strings }, (_, stringIdx) => stringIdx).map((stringIdx) => {
            const y = 45 + stringIdx * STRING_SPACING;
            const thickness = instrument === 'banjo'
              ? (stringIdx === 0 ? 1 : (5 - stringIdx) * 0.5 + 1)
              : instrument === 'guitar'
                ? (6 - stringIdx) * 0.4 + 0.8
                : (4 - stringIdx) * 0.5 + 1;
            
            // Fifth string handling (banjo only)
            const isBanjoFifth = instrument === 'banjo' && stringIdx === 0;
            const fifthStringEffectiveStart = isBanjoFifth ? Math.max(FIFTH_STRING_START, capo) : 0;
            const stringStartX = isBanjoFifth
              ? 80 + fifthStringEffectiveStart * 52 
              : (capo > 0 ? 78 + capo * 52 + 4 : 60);
            
            return (
              <g key={stringIdx}>
                <line 
                  x1={stringStartX} y1={y} 
                  x2={80 + (NUM_FRETS + 0.5) * 52} y2={y} 
                  stroke="#DAA520" strokeWidth={thickness} 
                />
                <text x={5} y={y + 4} fill="#888" fontSize="10">{stringLabels[stringIdx]}</text>
                
                {/* Open string note */}
                {(() => {
                  // Decide the active note + its visual fret position
                  let openFret: number;
                  let openNote: string;
                  let cx: number;
                  let strokeWidth = 2;
                  if (isBanjoFifth && capo < FIFTH_STRING_START) {
                    openNote = openStrings[stringIdx];
                    openFret = FIFTH_STRING_START;
                    cx = 80 + FIFTH_STRING_START * 52 - 26;
                  } else if (isBanjoFifth && capo >= FIFTH_STRING_START) {
                    openNote = getCapoOpenNote(stringIdx);
                    openFret = capo;
                    cx = 80 + capo * 52 - 26;
                  } else if (capo === 0) {
                    openNote = getCapoOpenNote(stringIdx);
                    openFret = 0;
                    cx = 65;
                    strokeWidth = 1.5;
                  } else {
                    openNote = getCapoOpenNote(stringIdx);
                    openFret = capo;
                    cx = 80 + capo * 52 - 26;
                  }
                  // In Theory mode, hide if out of range or not in scale
                  if (mode === 'theory') {
                    if (!isFretInRange(openFret) || !isTheoryNoteVisible(openNote)) return null;
                  }
                  const label = mode === 'theory' ? getTheoryLabel(openNote) : getNoteName(openNote);
                  const labelFontSize = mode === 'theory' ? 9 : 11;
                  return (
                    <g onClick={() => handleNoteClick(stringIdx, openFret, openNote)} style={{ cursor: 'pointer' }}>
                      <circle cx={cx} cy={y} r={12}
                        fill={activeNotes.has(openNote) ? '#22c55e' : (getTheoryFill(openNote) || 'transparent')}
                        stroke={activeNotes.has(openNote) ? '#22c55e' : getTheoryStroke(openNote)} strokeWidth={strokeWidth}
                      />
                      <text x={cx} y={y + 4} textAnchor="middle"
                        fill={getTheoryTextFill(openNote, activeNotes.has(openNote))}
                        fontSize={labelFontSize} fontWeight="bold"
                      >{label}</text>
                    </g>
                  );
                })()}
                
                {/* Fretted notes */}
                {Array.from({ length: NUM_FRETS }, (_, fret) => fret + 1).map(fret => {
                  if (fret <= capo) return null;
                  if (isBanjoFifth && fret < FIFTH_STRING_START) return null;
                  if (fret === capo) return null;
                  const note = getNoteAt(stringIdx, fret);
                  const noteName = getNoteName(note);
                  const x = 80 + fret * 52 - 26;
                  const isActive = activeNotes.has(note);

                  // In Theory mode: hide notes outside fret range or not in scale/chord
                  if (mode === 'theory') {
                    if (!isFretInRange(fret) || !isTheoryNoteVisible(note)) return null;
                  }

                  const label = mode === 'theory' ? getTheoryLabel(note) : noteName;
                  const labelFontSize = mode === 'theory' ? 8 : (config.fretless ? 9 : 10);

                  if (config.fretless) {
                    // Fiddle: show L markers
                    return (
                      <g key={fret} onClick={() => handleNoteClick(stringIdx, fret, note)} style={{ cursor: 'pointer' }}>
                        <circle cx={x} cy={y} r={12} 
                          fill={getTheoryCircleFill(note, isActive)}
                          stroke={getTheoryStroke(note)} 
                          strokeWidth={1.5} opacity={0.9}
                        />
                        <text x={x} y={y + 4} textAnchor="middle" 
                          fill={getTheoryTextFill(note, isActive)} fontSize={labelFontSize} fontWeight="bold"
                        >
                          {label}
                        </text>
                      </g>
                    );
                  }
                  
                  return (
                    <g key={fret} onClick={() => handleNoteClick(stringIdx, fret, note)} style={{ cursor: 'pointer' }}>
                      <circle cx={x} cy={y} r={12} 
                        fill={getTheoryCircleFill(note, isActive)}
                        stroke={mode === 'theory' ? getTheoryStroke(note) : (mode === 'compose' ? '#00ff88' : '#0f3460')} 
                        strokeWidth={1.5} opacity={0.9}
                      />
                      <text x={x} y={y + 4} textAnchor="middle" 
                        fill={getTheoryTextFill(note, isActive)} fontSize={labelFontSize} fontWeight="bold"
                      >
                        {label}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })}
          
          {/* Fret numbers */}
          {Array.from({ length: NUM_FRETS }, (_, i) => i + 1).map(fret => {
            if (fret <= capo) return null;
            return <text key={fret} x={80 + fret * 52 - 26} y={SVG_HEIGHT - 5} textAnchor="middle" fill="#666" fontSize="12">{fret}</text>;
          })}
        </svg>
          );
        })()}
      </div>

      {mode === 'compose' && (
        <p style={{ textAlign: 'center', color: '#00ff88', fontSize: '12px', margin: '2px 0' }}>
          ✏️ Compose mode — click notes to add to sequence
        </p>
      )}

      {/* Playback Controls */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', margin: '10px 0', flexWrap: 'wrap' }}>
        {!isPlaying ? (
          mode === 'theory' ? (
            <button style={styles.btn()} onClick={playTheoryScale}>
              ▶️ Play {theoryChord !== 'none' ? (CHORDS[theoryChord]?.label || 'Chord') : (SCALES[theoryScale]?.label.replace(/^⭐\s*/, '') || 'Scale')}
            </button>
          ) : (
            <button style={styles.btn()} onClick={() => playSequence()} disabled={sequence.length === 0}>
              ▶️ Play {selectedNotes.size > 0 ? `(${selectedNotes.size} selected)` : sequence.length > 0 ? `(${sequence.length} notes)` : ''}
            </button>
          )
        ) : (
          <button style={styles.btn(true)} onClick={stopPlayback}>⏹ Stop</button>
        )}
        <button 
          style={styles.btn(isLooping)} 
          onClick={() => setIsLooping(!isLooping)}
        >
          🔁 Loop {isLooping ? 'ON' : 'OFF'}
        </button>
        {mode === 'theory' && (
          <>
            <button
              style={styles.btn(droneOn)}
              onClick={() => setDroneOn(v => !v)}
              title={`Drone the root of ${theoryKey} — one octave below the lowest open string of the current tuning, stacked with the octave above`}
            >
              {droneOn ? `⏹ Stop Drone` : `🎻 Drone ${theoryKey}`}
            </button>
            <button
              style={styles.btn(droneFifthOn)}
              onClick={() => setDroneFifthOn(v => !v)}
              title="Add the fifth above the root drone (bagpipe / hurdy-gurdy flavor)"
            >
              🎶 +5th {droneFifthOn ? 'ON' : 'OFF'}
            </button>
            <button
              style={styles.btn(droneOctaveOn)}
              onClick={() => setDroneOctaveOn(v => !v)}
              title="Add the octave above each active drone voice (richer, cello-section body)"
            >
              🔊 +Octave {droneOctaveOn ? 'ON' : 'OFF'}
            </button>
          </>
        )}
        <button 
          style={styles.btn(metronomeState !== 'off')} 
          onClick={cycleMetronomeState}
        >
          {metronomeLabel}
        </button>
        {mode === 'compose' && (
          <>
            <button style={styles.btnSmall} onClick={clearSequence}>🗑️ Clear</button>
            <button style={styles.btnSmall} onClick={() => setShowSaveDialog(true)} disabled={sequence.length === 0}>
              💾 Save
            </button>
          </>
        )}
        {mode !== 'theory' && (
          <button 
            style={{ ...styles.btn(showLibrary), background: showLibrary ? '#e94560' : '#2a6e3f' }} 
            onClick={() => { setShowLibrary(!showLibrary); if (!showLibrary) refreshLibrary(); }}
          >
            📚 Melody Library
          </button>
        )}
      </div>

      {/* Metronome / Count-in Options */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', margin: '5px 0', flexWrap: 'wrap' }}>
        <button
          style={styles.btn(doubleCountIn)}
          onClick={() => setDoubleCountIn(!doubleCountIn)}
          title={doubleCountIn ? '2-measure count-in (sparse first measure)' : '1-measure count-in'}
        >
          {doubleCountIn ? '🎬 2-Bar Count-in' : '🎬 1-Bar Count-in'}
        </button>
        {isLooping && (
          <button
            style={styles.btn(loopCountIn)}
            onClick={() => setLoopCountIn(!loopCountIn)}
            title="Count in before each loop repeat"
          >
            🔄 Loop Count-in {loopCountIn ? 'ON' : 'OFF'}
          </button>
        )}
      </div>

      {/* Melody Library Panel */}
      {showLibrary && (
        <div style={{ margin: '10px 0', padding: '12px', background: '#16213e', borderRadius: '8px', border: '1px solid #2a6e3f' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#2a6e3f' }}>📚 Melody Library</span>
            <input
              style={{ ...styles.input, flex: 1 }}
              placeholder="Search melodies..."
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
            />
            <button style={styles.btnSmall} onClick={refreshLibrary}>🔄</button>
            <button style={styles.btnSmall} onClick={() => { setShowLibrary(false); setLibrarySearch(''); }}>✕</button>
          </div>
          
          {filteredLibrary.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', margin: '15px 0', fontSize: '13px' }}>
              {melodyLibrary.length === 0 
                ? 'No melodies yet — send Paige an audio file to get started! 🐾'
                : `No results for "${librarySearch}"`
              }
            </p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {filteredLibrary.map((melody, idx) => (
                <div key={idx}
                  style={{ 
                    display: 'inline-flex', alignItems: 'center', gap: '8px', 
                    padding: '8px 12px', margin: '2px', background: '#0f3460', 
                    border: '1px solid #2a6e3f', borderRadius: '6px', cursor: 'pointer' 
                  }}
                >
                  <span 
                    style={{ color: '#eee', fontWeight: 'bold' }}
                    onClick={() => loadMelody(melody)}
                  >🎵 {melody.name}</span>
                  <span style={{ fontSize: '11px', color: '#888' }}>
                    Key: {melody.key} • {melody.tuning || 'Open G'}{melody.capo ? ` • Capo ${melody.capo}` : ''} • {melody.bpm}bpm • {melody.notes.length} notes
                  </span>
                  <button
                    style={{ ...styles.btnDanger, fontSize: '10px', padding: '2px 6px' }}
                    onClick={(e) => { e.stopPropagation(); deleteFromLibrary(melody.name); }}
                    title="Delete from library"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transpose Controls */}
      {loadedMelody && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', margin: '8px 0', padding: '8px 12px', background: '#16213e', borderRadius: '8px', border: '1px solid #2a6e3f' }}>
          <span style={{ fontSize: '13px', color: '#2a6e3f', fontWeight: 'bold' }}>🎵 {loadedMelody.name}</span>
          <button style={styles.btnSmall} onClick={() => applyTranspose(transposeSemitones - 1)}>−</button>
          <span style={{ fontSize: '14px', color: '#fff', fontWeight: 'bold', minWidth: '120px', textAlign: 'center' }}>
            Key: {getTransposedKey(loadedMelody.key, transposeSemitones)}
            {transposeSemitones !== 0 && (
              <span style={{ fontSize: '11px', color: '#888' }}> ({transposeSemitones > 0 ? '+' : ''}{transposeSemitones})</span>
            )}
          </span>
          <button style={styles.btnSmall} onClick={() => applyTranspose(transposeSemitones + 1)}>+</button>
          <button style={{ ...styles.btnSmall, marginLeft: '8px' }} onClick={() => applyTranspose(0)} disabled={transposeSemitones === 0}>Reset</button>
          <button style={{ ...styles.btnSmall, marginLeft: '4px' }} onClick={() => { setLoadedMelody(null); setTransposeSemitones(0); }}>✕</button>
        </div>
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <div style={{ textAlign: 'center', margin: '10px 0', padding: '10px', background: '#16213e', borderRadius: '8px' }}>
          <input 
            style={styles.input}
            placeholder="Riff name..."
            value={riffName}
            onChange={(e) => setRiffName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveRiff()}
            autoFocus
          />
          <button style={{ ...styles.btn(), marginLeft: '8px' }} onClick={saveRiff}>Save</button>
          <button style={{ ...styles.btnSmall, marginLeft: '8px' }} onClick={() => setShowSaveDialog(false)}>Cancel</button>
        </div>
      )}

      {/* Sequence Timeline (hidden in Theory mode) */}
      {mode !== 'theory' && (
      <div style={styles.sequenceContainer}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: '#888', fontWeight: 'bold' }}>
            📝 Sequence {sequence.length > 0 ? `(${sequence.reduce((sum, n) => sum + n.beats, 0).toFixed(1)} beats • ${timeSignature})` : ''}
          </span>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666' }}>Measures/row:</span>
            {[2, 4, 8].map(n => (
              <button
                key={n}
                style={{
                  ...styles.btnSmall,
                  background: measuresPerRow === n ? '#e94560' : '#0f3460',
                  fontWeight: measuresPerRow === n ? 'bold' : 'normal',
                }}
                onClick={() => setMeasuresPerRow(n)}
              >
                {n}
              </button>
            ))}
          </div>
          {mode === 'compose' && (
            <span style={{ fontSize: '11px', color: '#666' }}>
              Click: duration • Long-click: pitch • Right-click: remove • Shift+click: select
            </span>
          )}
        </div>
        
        {sequence.length === 0 ? (
          <p style={{ color: '#555', textAlign: 'center', margin: '15px 0', fontSize: '14px' }}>
            {mode === 'compose' ? 'Click notes on the fretboard to start composing...' : 'Switch to Compose mode to add notes'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'visible', paddingTop: '50px', marginTop: '-50px' }}>
            {buildRows().map((row, rowIdx) => (
              <div key={rowIdx} style={{ display: 'flex', marginBottom: '6px', alignItems: 'stretch' }}>
                {row.map((measure, mIdx) => {
                  const measureNum = rowIdx * measuresPerRow + mIdx + 1;
                  const isEvenMeasure = measureNum % 2 === 0;
                  return (
                    <div key={mIdx} style={{ 
                      display: 'flex', 
                      flexShrink: 0,
                      background: isEvenMeasure ? '#0d1b3e' : '#0f1f45',
                      borderLeft: `${BAR_LINE_WIDTH}px solid #555`,
                      borderRight: mIdx === row.length - 1 ? `${BAR_LINE_WIDTH}px solid #555` : 'none',
                      position: 'relative',
                      minWidth: slotsPerMeasure * SLOT_WIDTH,
                    }}>
                      <span style={{ position: 'absolute', top: '-1px', left: '3px', fontSize: '9px', color: '#555' }}>
                        {measureNum}
                      </span>
                      {Array.from({ length: beatsPerMeasure - 1 }, (_, bIdx) => (
                        <div key={bIdx} style={{
                          position: 'absolute',
                          left: `${(bIdx + 1) * 8 * SLOT_WIDTH}px`,
                          top: 0,
                          bottom: 0,
                          width: '1px',
                          background: '#333',
                        }} />
                      ))}
                      {measure.notes.map((item, nIdx) => {
                        const { note, slots, seqIdx } = item;
                        const isActive = playingIndex === seqIdx;
                        const isSelected = selectedNotes.has(note.id);
                        const dur = DURATIONS.find(d => d.value === note.duration) || DURATIONS[2];
                        const isPitchEditing = pitchEditId === note.id;
                        
                        return (
                          <div
                            key={`${note.id}-${nIdx}`}
                            style={{
                              width: `${slots * SLOT_WIDTH}px`,
                              height: '52px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: isActive ? '#22c55e' : isSelected ? '#1a3a5c' : 'transparent',
                              border: isSelected ? '1px solid #4a9eff' : '1px solid transparent',
                              borderRadius: '3px',
                              cursor: mode === 'compose' ? 'pointer' : 'default',
                              position: 'relative',
                              flexShrink: 0,
                              transition: 'background 0.15s',
                              userSelect: 'none',
                            }}
                            onClick={(e) => {
                              if (e.shiftKey) {
                                if (selectionAnchor === null) {
                                  setSelectionAnchor(seqIdx);
                                  setSelectedNotes(new Set([note.id]));
                                } else {
                                  const start = Math.min(selectionAnchor, seqIdx);
                                  const end = Math.max(selectionAnchor, seqIdx);
                                  const rangeIds = new Set(sequence.slice(start, end + 1).map(n => n.id));
                                  setSelectedNotes(rangeIds);
                                }
                                setPitchEditId(null);
                                return;
                              }
                              if (selectedNotes.has(note.id)) {
                                setPitchEditId(pitchEditId === note.id ? null : note.id);
                                return;
                              }
                              if (mode !== 'compose') return;
                              const currentIdx = DURATIONS.findIndex(d => d.value === note.duration);
                              const nextIdx = (currentIdx + 1) % DURATIONS.length;
                              updateNoteDuration(note.id, DURATIONS[nextIdx]);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              if (mode === 'compose') removeNote(note.id);
                            }}
                          >
                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: note.isRest ? '#555' : (isActive ? '#fff' : '#ddd') }}>
                              {note.isRest ? '𝄾' : getNoteName(note.note)}
                            </span>
                            {!note.isRest && (
                              <span style={{ fontSize: '9px', color: '#777' }}>
                                {note.note.replace(/([A-G]#?)(\d)/, '$1$2')}
                              </span>
                            )}
                            <span style={{ fontSize: '12px', color: '#888' }}>{dur.label}</span>
                            
                            {isPitchEditing && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '-44px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  display: 'flex',
                                  gap: '4px',
                                  background: '#0a0a1a',
                                  border: '1px solid #e94560',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  zIndex: 10,
                                  whiteSpace: 'nowrap',
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onContextMenu={(e) => e.stopPropagation()}
                              >
                                {note.isRest ? (
                                  <>
                                    <span style={{ fontSize: '12px', color: '#888', alignSelf: 'center' }}>rest</span>
                                    <button
                                      style={{ ...styles.btnSmall, fontSize: '11px', padding: '2px 8px' }}
                                      onClick={(e) => { e.stopPropagation(); convertFromRest(note.id); }}
                                      title="Convert to note"
                                    >♪ Note</button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      style={{ ...styles.btnSmall, fontSize: '14px', padding: '2px 8px' }}
                                      onClick={(e) => { e.stopPropagation(); shiftPitch(note.id, -1); }}
                                      title="Down 1 semitone"
                                    >▼</button>
                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#e94560', alignSelf: 'center', minWidth: '32px', textAlign: 'center' }}>
                                      {getNoteName(note.note)}{note.note.match(/\d+/)?.[0]}
                                    </span>
                                    <button
                                      style={{ ...styles.btnSmall, fontSize: '14px', padding: '2px 8px' }}
                                      onClick={(e) => { e.stopPropagation(); shiftPitch(note.id, 1); }}
                                      title="Up 1 semitone"
                                    >▲</button>
                                    <button
                                      style={{ ...styles.btnSmall, fontSize: '10px', padding: '2px 6px', color: '#888' }}
                                      onClick={(e) => { e.stopPropagation(); convertToRest(note.id); }}
                                      title="Convert to rest"
                                    >𝄾</button>
                                  </>
                                )}
                                <button
                                  style={{ ...styles.btnSmall, fontSize: '10px', padding: '2px 6px' }}
                                  onClick={(e) => { e.stopPropagation(); setPitchEditId(null); }}
                                >✕</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {measure.filledSlots < slotsPerMeasure && (
                        <div style={{ 
                          width: `${(slotsPerMeasure - measure.filledSlots) * SLOT_WIDTH}px`,
                          flexShrink: 0,
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      )}

      {/* Saved Riffs (hidden in Theory mode) */}
      {mode !== 'theory' && savedRiffs.length > 0 && (
        <div style={{ margin: '15px 0', padding: '10px', background: '#16213e', borderRadius: '8px' }}>
          <span style={{ fontSize: '13px', color: '#888', fontWeight: 'bold' }}>📁 Saved Riffs</span>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {savedRiffs.map((riff, idx) => (
              <div key={idx} style={styles.savedRiff as React.CSSProperties}>
                <span 
                  onClick={() => loadRiff(riff)} 
                  style={{ cursor: 'pointer', color: '#eee', fontWeight: 'bold' }}
                >
                  🎵 {riff.name}
                </span>
                <span style={{ fontSize: '11px', color: '#666' }}>
                  {riff.notes.length} notes • {riff.bpm}bpm
                </span>
                <button
                  style={{ ...styles.btnSmall, background: '#2a6e3f', border: '1px solid #2a6e3f' }}
                  onClick={(e) => { e.stopPropagation(); transferToLibrary(riff); }}
                  title="Transfer to Melody Library"
                >
                  📚
                </button>
                <button 
                  style={styles.btnDanger} 
                  onClick={(e) => { e.stopPropagation(); deleteRiff(idx); }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ textAlign: 'center', color: '#444', fontSize: '11px', marginTop: '15px' }}>
        StringForge v1.0 • Built by Jamie & Paige 🐾
      </p>
    </div>
  );
};

export default BanjoFretboard;
